import { faker } from "@faker-js/faker";
import { BlueText } from "components/common";
import { COLORS } from "constant";
import usePanelConfiguration from "hooks/usePanelConfiguration";
import _, { size, get, groupBy, keys } from "lodash";
import { useState, useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { displaySensorType } from "utils/functions";
import { ColorPicker, getMQTTQueryByDevice } from "../create";
import DashboardCreateSelectSensorModal from "components/dashboard/create/modal/SelectDevices";
import { NoInformationColumn } from "components/common/NoInformationColumn";
import deleteSvg from "assets/svg/delete.svg";
import {
    getConsumptionCLSFromMetricName,
    getSensorType,
} from "utils/sensorFunctions";
import { SensorType } from "constant/gateway";
import CloseIcon from "@mui/icons-material/Close";

const ChartType = {
    [SensorType.WaterConsumption]: "Water consumption",
    [SensorType.EnergyConsumption]: "Energy consumption",
};

const CombinedConsumtionChartUpdate = ({
    panel,
    devices,
    isMutipleEdit = false,
    onChange,
    onDelete,
}: {
    panel: any;
    devices: any;
    isMutipleEdit?: boolean;
    onChange: Function;
    onDelete?: Function;
}) => {
    const { attributes, uuid } = panel;

    const { defaultValue } = usePanelConfiguration({
        panelData: panel,
        deviceList: devices,
    });

    const [chartName, setChartName] = useState(panel.name);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [chartType, setChartType] = useState("");
    const [selectedDevices, setSelectedDevices] = useState<{
        [key: string]: any;
    }>({});
    const [filterType, setFilterType] = useState<string[]>([]);

    const title = chartType ? `Sensors for Combined - ${chartType} chart` : "";

    const canRemove =
        (isMutipleEdit && size(selectedDevices) > 2) || !isMutipleEdit;

    useEffect(() => {
        if (devices.length && attributes) {
            const attributeList = attributes.map((item: any) => {
                const sensorCls = getConsumptionCLSFromMetricName(
                    item.time_series
                );

                const device = devices?.find(
                    (device: any) =>
                        device.device_id === item.device_id + "/" + item.said
                );

                return {
                    key: `${item?.device_id}/${item.said}`,
                    ...device,
                    ...item,
                    device_id: `${item.device_id}/${item.said}`,
                    color: item.color,
                    CLS: sensorCls,
                };
            });

            const deviceTypes = groupBy(attributeList, "sensor_type");
            setSelectedDevices(attributeList.pluck("device_id"));
            setFilterType(keys(deviceTypes));

            const sensorType = getSensorType({ CLS: attributeList[0].CLS });
            const chartType = get(ChartType, sensorType ?? "", "");
            setChartType(chartType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const mapChartData = (devices: any[]) => {
        return devices.map((device) => {
            const {
                gatewayId,
                gateway_id,
                device_id,
                ldsu_uuid,
                said,
                SAID,
                show_alert,
                time_series,
                unit,
                color,
                sensor_type,
            } = device;
            return {
                gateway_id: gateway_id ?? gatewayId,
                ldsu_uuid,
                said: said ?? SAID,
                show_alert,
                time_series: time_series ?? getMQTTQueryByDevice(device),
                unit,
                color,
                name: sensor_type,
                device_id,
            };
        });
    };

    const onChangeDevice = (data: any) => {
        const devices = Object.values(data).map((item: any, index: number) => ({
            ...item,
            color: get(
                selectedDevices,
                `[${item.device_id}].color`,
                COLORS[index] ||
                    faker.color.rgb({
                        format: "hex",
                    })
            ),
            alert: false,
            device_id: `${item.ldsu_uuid}/${item.SAID}`,
        }));
        setSelectedDevices(devices.pluck("device_id"));
        onChange({
            chartName: chartName,
            data: mapChartData(Object.values(devices)),
        });
    };

    const onRemove = (device: any) => {
        const devices = _.omit(selectedDevices, device.device_id);
        setSelectedDevices(devices);
        onChange({
            chartName: chartName,
            data: mapChartData(Object.values(devices)),
        });
    };

    const onChangeColor = (device: any, color: any) => {
        const devices = _.set(
            selectedDevices,
            `${device.device_id}.color`,
            color
        );

        setSelectedDevices(devices);
        onChange({
            chartName: chartName,
            data: mapChartData(Object.values(devices)),
        });
    };

    const onChangeChartName = (name: string) => {
        setChartName(name);
        onChange({
            chartName: name,
        });
    };

    return (
        <div className="multiple-chart-block form-box mb-3">
            <Row>
                <Col xs={12} sm="11">
                    <Form.Group>
                        <Form.Label>Chart Name</Form.Label>
                        <Form.Control
                            name="name"
                            type="text"
                            value={chartName}
                            placeholder=""
                            onChange={(event) =>
                                onChangeChartName(event.target.value)
                            }
                        />
                    </Form.Group>

                    <p>{title}</p>
                    <div className="my-2">
                        <Button
                            className="mr-3"
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        <BlueText>You can add max 8 sensors</BlueText>
                    </div>
                    {size(selectedDevices) === 1 && (
                        <span className="text-danger">
                            Please select at least two sensors to create a
                            combined consumption chart.
                        </span>
                    )}

                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        sensorTypes={filterType}
                        onSave={(event, data) => onChangeDevice(data)}
                        deviceType="Sensor"
                    />
                </Col>
                {isMutipleEdit && (
                    <Col
                        sm={1}
                        className="delete-block d-flex align-items-center"
                    >
                        <img
                            src={deleteSvg}
                            onClick={() => onDelete?.(uuid)}
                            onKeyDown={() => onDelete?.(uuid)}
                            className="multiple-chart-block--delete"
                            alt="delete"
                        />
                    </Col>
                )}
            </Row>
            <Row className="mt-4 cstm-table ">
                <Col sm={12}>
                    {!!Object.values(selectedDevices).length && (
                        <div className="table-head">
                            <Row className="no-checkbox">
                                <Col md={2}>Sensor</Col>
                                <Col md={2}>Type</Col>
                                <Col md={3} sm={2}>
                                    Gateway
                                </Col>
                                <Col md={3}>LDSU UUID</Col>
                                <Col lg={1} sm={2}>
                                    Color
                                </Col>
                                <Col md={1}></Col>
                            </Row>
                        </div>
                    )}
                    {Object.values(selectedDevices).map((device, index) => (
                        <div
                            key={getId()}
                            id={device.device_id}
                            className="table-row"
                        >
                            <Row className="no-checkbox">
                                <Col
                                    className="h-auto"
                                    md={device.device_name ? 2 : 11}
                                >
                                    {device.device_name}
                                    {!device.device_name && (
                                        <NoInformationColumn />
                                    )}
                                </Col>
                                {device.device_name && (
                                    <>
                                        <Col className="h-auto" md={2}>
                                            {displaySensorType(device)}
                                        </Col>
                                        <Col className="h-auto" lg={3} sm={2}>
                                            {device.gatewayName}
                                        </Col>
                                        <Col className="h-auto" md={3}>
                                            {device.ldsu_uuid}
                                        </Col>
                                        <Col className="h-auto" sm={2} lg={1}>
                                            <ColorPicker
                                                defaultValue={get(
                                                    selectedDevices,
                                                    `${device.device_id}.color`,
                                                    COLORS[index]
                                                )}
                                                onChange={(event, color) =>
                                                    onChangeColor(device, color)
                                                }
                                            />
                                        </Col>
                                    </>
                                )}
                                {canRemove && (
                                    <Col
                                        md={1}
                                        className="d-flex justify-content-end h-auto"
                                    >
                                        <CloseIcon
                                            className="cursor-pointer mr-2 text-primary-red-1"
                                            onClick={() => onRemove(device)}
                                            onKeyDown={() => onRemove(device)}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </div>
                    ))}
                </Col>
            </Row>
        </div>
    );
};

export default CombinedConsumtionChartUpdate;
